import { useState } from "react";
import { X, Calendar, Download, FileSpreadsheet, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Capacitor } from "@capacitor/core";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";

// Helper: Convert Blob to clean Base64 string with Promise
const blobToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const result = reader.result;
      const base64 = typeof result === "string" ? result.split(",")[1] : "";
      resolve(base64);
    };
    reader.readAsDataURL(blob);
  });

export default function ExportModal({ isOpen, onClose }) {
  const [filterType, setFilterType] = useState("all"); // 'all' | 'today' | 'custom'
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [downloading, setDownloading] = useState(false);

  if (!isOpen) return null;

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);

    try {
      let url = "/leads/export/xlsx";
      const params = new URLSearchParams();

      if (filterType === "today") {
        const todayStr = new Date().toISOString().split("T")[0];
        params.append("start_date", todayStr);
        params.append("end_date", todayStr);
      } else if (filterType === "custom") {
        if (!startDate || !endDate) {
          toast.error("Please select both From and To dates");
          setDownloading(false);
          return;
        }
        if (startDate > endDate) {
          toast.error("Start date cannot be after end date");
          setDownloading(false);
          return;
        }
        params.append("start_date", startDate);
        params.append("end_date", endDate);
      }

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await api.get(url, { responseType: "blob" });
      const fileName = `expolead_export_${new Date().toISOString().slice(0, 10)}.xlsx`;

      // 1. Native Mobile (Capacitor Android / iOS)
      if (Capacitor.isNativePlatform()) {
        const base64Data = await blobToBase64(response.data);

        // Write file to Cache directory
        const savedFile = await Filesystem.writeFile({
          path: fileName,
          data: base64Data,
          directory: Directory.Cache,
        });

        try {
          await Share.share({
            title: "ExpoLead Excel Export",
            text: "ExpoLead trade show pipeline report",
            url: savedFile.uri,
            dialogTitle: "Save or Share Excel Report",
          });
          toast.success("Excel report exported successfully!");
        } catch (shareErr) {
          // Ignore normal dismissal when user closes the native share sheet
          const msg = shareErr?.message?.toLowerCase() || "";
          if (msg.includes("cancel") || shareErr?.name === "AbortError") {
            toast.info("Export file prepared in device storage.");
          } else {
            console.warn("Native share notice:", shareErr);
          }
        }
        onClose();
      } 
      // 2. Web Browser (Desktop & Mobile Web)
      else {
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();

        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(downloadUrl);
        }, 150);

        toast.success("Excel report downloaded successfully!");
        onClose();
      }
    } catch (err) {
      if (err?.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const json = JSON.parse(text);
          toast.error(json.detail || "Failed to export Excel report.");
        } catch {
          toast.error("Failed to export Excel report.");
        }
      } else {
        toast.error(err?.response?.data?.detail || "Failed to export Excel report.");
      }
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={!downloading ? onClose : undefined}
    >
      <div 
        className="w-full max-w-md bg-white rounded-3xl p-6 shadow-2xl border border-slate-100 space-y-5"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="h-10 w-10 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center border border-emerald-100">
              <FileSpreadsheet className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-lg font-black text-slate-900 tracking-tight">Export to Excel</h3>
              <p className="text-xs text-slate-500 font-medium">Select date range for lead export</p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={downloading}
            className="p-2 text-slate-400 hover:text-slate-700 rounded-full hover:bg-slate-100 transition cursor-pointer disabled:opacity-50"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Filter Selection Tabs */}
        <div className="grid grid-cols-3 gap-1.5 p-1 bg-slate-100 rounded-2xl">
          {[
            { id: "all", label: "All Time" },
            { id: "today", label: "Today" },
            { id: "custom", label: "Custom Range" },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setFilterType(tab.id)}
              disabled={downloading}
              className={`py-2 text-xs font-bold rounded-xl transition cursor-pointer disabled:opacity-50 ${
                filterType === tab.id
                  ? "bg-white text-blue-600 shadow-sm"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Custom Date Pickers */}
        {filterType === "custom" && (
          <div className="grid grid-cols-2 gap-3 p-4 bg-slate-50 border border-slate-100 rounded-2xl animate-in fade-in duration-200">
            <div className="space-y-1">
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">From Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                disabled={downloading}
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-medium text-slate-800 focus:outline-none focus:border-blue-600"
              />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">To Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                disabled={downloading}
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-medium text-slate-800 focus:outline-none focus:border-blue-600"
              />
            </div>
          </div>
        )}

        {/* Modal Actions */}
        <div className="flex items-center gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={downloading}
            className="flex-1 h-12 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs transition cursor-pointer active:scale-95 disabled:opacity-50"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={handleDownload}
            disabled={downloading}
            className="flex-1 h-12 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs flex items-center justify-center gap-2 shadow-lg shadow-blue-600/20 transition cursor-pointer active:scale-95 disabled:opacity-60"
          >
            {downloading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Generating…</span>
              </>
            ) : (
              <>
              
                <Download className="h-4 w-4" />
                <span>Download XLSX</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}